import { useEffect, useState } from "react";
import { Button, Modal } from "semantic-ui-react";
import { FormError } from "../models/FormError";
import AddNewVersionToDocumentForm, { FormData } from "./AddNewVersionToDocumentForm";
import { UserFile } from "../models/UserFile";
import { FileMetaData } from "../models/FileMetaData";
import { Document_Data } from "../models/Document_Data";

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (id: string, title: string, versionNo: number, fileMetaData: FileMetaData) => void;
  submitInProgress: boolean;
  submitErrors?: string[];
  document?: Document_Data;
  userFile?: UserFile;
  onUploadFile: (userFile: UserFile) => void;
  uploading: boolean;
}

const AddNewVersionToDocumentModal = ({
  open,
  onClose,
  onSubmit,
  submitInProgress,
  submitErrors,
  document,
  userFile,
  onUploadFile,
  uploading,
}: Props) => {
  const [formData, setFormData] = useState<FormData>({} as FormData);
  const [formErrors, setFormErrors] = useState<FormError>({});
  const [hasSubmitted, setHasSubmitted] = useState(false);

  useEffect(() => {
    if (open) {
      setFormData({
        title: document?.title,
        id: document?.id,
        versionNo: document ? Math.trunc(document.versionNo + 1) : "1",
      } as FormData);
    } else {
      setFormData({} as FormData);
      setFormErrors({} as FormError);
      setHasSubmitted(false);
    }
  }, [open, document]);

  useEffect(() => {
    if (!userFile?.uploadStatus) return;

    if (userFile.uploadStatus.uploaded) {
      setFormData({ ...formData, userFile });
    } else if (userFile.uploadStatus.uploadError) {
      setFormErrors({
        ...formErrors,
        userFile: userFile.uploadStatus?.uploadError.message,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userFile]);

  const validateFormData = (formData: FormData) => {
    const formError: FormError = {};

    if (!formData.title) {
      formError.title = "Please enter title";
    }

    if (!formData.versionNo) {
      formError.versionNo = "Please enter version";
    }

    if (!formData.userFile || !formData.userFile.uploadStatus.uploaded || formData.userFile.uploadStatus.uploadError) {
      formError.userFile = "Please select a file";
    }

    return formError;
  };

  const handleSubmit = () => {
    setHasSubmitted(true);

    const validationErrors = validateFormData(formData);
    setFormErrors(validationErrors);

    // If there is no validation error
    if (Object.keys(validationErrors).length === 0) {
      const { id, title, versionNo, userFile } = formData;
      if (userFile) {
        const fileMetaData = {
          id: userFile.id,
          fileName: userFile.file.name,
          contentType: userFile.file.type,
        } as FileMetaData;
        onSubmit(id, title, parseFloat(versionNo), fileMetaData);
      }
    }
  };

  const handleFormChange = (formData: FormData) => {
    if (hasSubmitted) {
      const validationErrors = validateFormData(formData);
      setFormErrors(validationErrors);
    }

    setFormData({ ...formData });
  };

  const handelClose = () => {
    onClose();
  };

  const handleAcceptedFilesSelected = (acceptedFiles: UserFile[]) => {
    if (acceptedFiles.length) {
      onUploadFile(acceptedFiles[0]);
      setFormErrors({});
    }
  };

  const handleRejectedFilesSelected = (rejectedFiles: UserFile[]) => {
    if (rejectedFiles.length) {
      setFormErrors({
        ...formErrors,
        userFile: rejectedFiles[0].uploadStatus?.fileErrors
          ? rejectedFiles[0].uploadStatus.fileErrors[0]
          : "Please select a file",
      });
    }
  };

  return (
    <Modal open={open}>
      <Modal.Header>New version</Modal.Header>
      <Modal.Content>
        <Modal.Description>
          <AddNewVersionToDocumentForm
            formErrors={formErrors}
            errors={submitErrors}
            formData={formData}
            onChange={handleFormChange}
            onAcceptedFilesSelected={handleAcceptedFilesSelected}
            onRejectedFilesSelected={handleRejectedFilesSelected}
          ></AddNewVersionToDocumentForm>
        </Modal.Description>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={handelClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          positive
          loading={submitInProgress || uploading}
          disabled={submitInProgress || uploading}
        >
          Ok
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default AddNewVersionToDocumentModal;
